import type { PIEntryLevel } from "../hooks/hierarchyState";

/**
 * The dashboard drill position that must survive a hard reload / be shareable
 * (#20932): which hierarchy level the user is viewing and the parent ids that
 * scope it. Display names are intentionally omitted — they backfill from the
 * fetched node data; only the ids are needed to re-issue the dashboard query.
 */
export interface DrillUrlState {
  level: PIEntryLevel;
  parentSite?: string;
  parentUnit?: string;
  parentSystem?: string;
  parentAf?: string;
  /** AF element id of the anchored component being viewed, when on an asset. */
  assetId?: string;
}

const DRILL_LEVELS: readonly string[] = [
  "site",
  "unit",
  "system",
  "af",
  "location",
];

export const drillStateToSearchParams = (
  state: DrillUrlState,
): URLSearchParams => {
  const params = new URLSearchParams();
  const atBaseView =
    state.level === "site" &&
    !state.parentSite &&
    !state.parentUnit &&
    !state.parentSystem &&
    !state.parentAf &&
    !state.assetId;
  if (atBaseView) return params;
  params.set("level", state.level);
  if (state.parentSite) params.set("site", state.parentSite);
  if (state.parentUnit) params.set("unit", state.parentUnit);
  if (state.parentSystem) params.set("system", state.parentSystem);
  if (state.parentAf) params.set("af", state.parentAf);
  if (state.assetId) params.set("asset", state.assetId);
  return params;
};

export const drillStateFromSearchParams = (
  params: URLSearchParams,
): DrillUrlState | null => {
  const level = params.get("level");
  if (!level || !DRILL_LEVELS.includes(level)) return null;
  // `params.get` returns "" for a present-but-empty value (e.g. ?site=), which
  // is not nullish and would slip past downstream `?? resumeAfElementId`-style
  // fallbacks. Map empty to undefined so decode mirrors the encoder, which only
  // emits a param when its value is truthy.
  return {
    level: level as PIEntryLevel,
    parentSite: params.get("site") || undefined,
    parentUnit: params.get("unit") || undefined,
    parentSystem: params.get("system") || undefined,
    parentAf: params.get("af") || undefined,
    assetId: params.get("asset") || undefined,
  };
};
